import { fetchWithRetry, responseError } from './apiError';
import { tokenService } from './tokenService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MODEL_CALL_ROUTING_URL = `${API_URL}/api/v1/admin/settings/model-call-routing`;
const MODEL_CALL_ROUTING_CACHE_MS = 30000;

export type CallRoute = 'probe_fallback' | 'direct_api' | 'oauth';

export interface CallTypeRouteInfo {
  callType: string;
  label: string;
  validRoutes: CallRoute[];
  route: CallRoute;
}

export interface ModelCallRouting {
  callTypes: CallTypeRouteInfo[];
}

interface CallTypeRouteInfoPayload {
  call_type: string;
  label: string;
  valid_routes: CallRoute[];
  route: CallRoute;
}

interface ModelCallRoutingPayload {
  call_types: CallTypeRouteInfoPayload[];
}

interface ModelCallRoutingCache {
  authToken: string | null;
  data: ModelCallRouting;
  fetchedAt: number;
  revision: number;
}

interface ModelCallRoutingRequest {
  authToken: string | null;
  id: symbol;
  promise: Promise<ModelCallRouting>;
}

let routingCache: ModelCallRoutingCache | null = null;
let routingRequest: ModelCallRoutingRequest | null = null;
let routingRevision = 0;

const fromPayload = (payload: ModelCallRoutingPayload): ModelCallRouting => ({
  callTypes: payload.call_types.map((row) => ({
    callType: row.call_type,
    label: row.label,
    validRoutes: row.valid_routes,
    route: row.route,
  })),
});

const fetchWithAuth = (options?: globalThis.RequestInit): Promise<globalThis.Response> => {
  const token = tokenService.getAccessToken();

  return fetchWithRetry(MODEL_CALL_ROUTING_URL, {
    ...options,
    headers: { ...options?.headers, Authorization: `Bearer ${token}` },
  });
};

const readResponse = async (response: globalThis.Response): Promise<ModelCallRouting> => {
  if (!response.ok) throw await responseError(response);

  return fromPayload(await response.json());
};

const cacheFor = (authToken: string | null): ModelCallRoutingCache | null => (
  routingCache?.authToken === authToken ? routingCache : null
);

const storeRouting = (
  authToken: string | null,
  data: ModelCallRouting,
  revision: number,
): void => {
  if (tokenService.getAccessToken() !== authToken) return;

  routingCache = { authToken, data, fetchedAt: Date.now(), revision };
};

const loadRouting = (
  authToken: string | null,
  revision: number,
): Promise<ModelCallRouting> => {
  const id = Symbol('model-call-routing-request');
  const promise = fetchWithAuth()
    .then(readResponse)
    .then((data) => {
      if (routingRevision === revision) storeRouting(authToken, data, revision);

      return data;
    })
    .finally(() => {
      if (routingRequest?.id === id) routingRequest = null;
    });

  routingRequest = { authToken, id, promise };

  return promise;
};

const getModelCallRouting = (): Promise<ModelCallRouting> => {
  const authToken = tokenService.getAccessToken();
  const cached = cacheFor(authToken);
  const cacheFresh = cached
    && cached.revision === routingRevision
    && Date.now() - cached.fetchedAt < MODEL_CALL_ROUTING_CACHE_MS;

  if (cacheFresh) return Promise.resolve(cached.data);
  if (routingRequest?.authToken === authToken) return routingRequest.promise;

  return loadRouting(authToken, routingRevision);
};

const setModelCallRouting = async (routing: Record<string, CallRoute>): Promise<ModelCallRouting> => {
  const authToken = tokenService.getAccessToken();
  const response = await fetchWithAuth({
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ routing }),
  });
  const data = await readResponse(response);

  routingRevision += 1;
  storeRouting(authToken, data, routingRevision);

  return data;
};

export const modelCallRoutingService = {
  getModelCallRouting,
  setModelCallRouting,
};
